import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout } from '../../components/BlogLayout';
import { HeroRail, PostGrid } from '../../components/PostFeed';
import { PageHeader } from '../../components/PageHeader';
import { api } from '../../lib/api';
import type { Post } from '../../lib/types';

const sections = ['Tech Lab', 'Business', 'Top Lists', 'Living', 'Steals', 'How Tos'];

function slugifyCategory(label: string) {
  return label
    .toLowerCase()
    .replace(/\+/g, '-plus')
    .replace(/\s+/g, '-');
}

export default function CategoriesPage() {
  const [searchParams] = useSearchParams();
  const focusSlug = (searchParams.get('focus') || '').toLowerCase();

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sectionList = sections.map((name) => ({ name, slug: slugifyCategory(name) }));
  const focused = focusSlug ? sectionList.find((s) => s.slug === focusSlug) : null;

  useEffect(() => {
    async function fetchPosts() {
      try {
        const response = await api.get<Post[]>('/api/v1/posts');
        if (response.success && response.data) {
          setPosts(response.data);
        } else {
          setError(response.error?.message || 'Failed to load posts');
        }
      } catch (err) {
        setError('Failed to load posts');
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  const filtered = focused
    ? posts.filter((p) => (p.category || '').trim().toLowerCase() === focused.name.toLowerCase())
    : posts;

  const feedPosts = filtered.length > 0 ? filtered : posts;
  const pageTitle = focused ? `${focused.name}` : 'Browse by topic';
  const heroPosts = feedPosts.slice(0, 2);
  const gridPosts = feedPosts.length > 2 ? feedPosts.slice(2) : feedPosts;

  return (
    <BlogLayout>
      <Helmet>
        <title>{focused ? `${focused.name} - ` : ''}Categories - QuestFlow</title>
        <meta name="description" content="Browse QuestFlow stories by category" />
      </Helmet>
      <div className="page-body">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading categories...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <PageHeader title={pageTitle} eyebrow="Categories" />
            <HeroRail posts={heroPosts} />
            <PostGrid
              posts={gridPosts}
              title={focused ? `${focused.name} Stories` : 'Latest Stories'}
              ctaHref={focused ? '/categories' : '/archive'}
              ctaLabel={focused ? 'All categories' : 'View all'}
            />
          </>
        )}
      </div>
    </BlogLayout>
  );
}
